import { useState } from 'react'
import { navListRootIndices } from '../../../../platform/client/navigation'
import Tile from './Tile'
import getAsset from '../../../../platform/getAsset'
import TextInput from './TextInput'

import './ScrollMenu.css'

type Option = {
  id: string | null
  name: string
  description?: string
  props?: { tileImageSrc?: string; hex?: string }
}

type SelectInputProps = {
  onChange: (id?: string | null) => void
  options: Option[]
  tileImageTemplate: (option: Option) => string
  tileType: string
  value: string
  visibleOptions?: Option[]
  isRequired: boolean
  object: Option
}

function SelectInput({
  onChange,
  options,
  tileImageTemplate,
  tileType,
  value,
  visibleOptions,
  isRequired,
  object: selectedOption,
}: SelectInputProps) {
  const [displayOption, setDisplayOption] = useState(selectedOption)

  let opts = visibleOptions ?? options

  if (!isRequired) {
    opts = [{ id: null, name: 'None' } as Option].concat(opts)
  }

  const isSquareWithText = tileType === 'squareWithText'
  const hasImageTemplate = !!tileImageTemplate

  return (
    <>
      <div className="select-input">
        {opts.map((option) => {
          let imageSrc
          if (option.id != null) {
            if (option.props?.tileImageSrc) {
              imageSrc = getAsset(option.props.tileImageSrc)
            } else if (hasImageTemplate) {
              imageSrc = getAsset(tileImageTemplate(option))
            }
          }
          const dashed = option.id == null || option.id === 'no'
          return (
            <Tile
              background={dashed ? 'transparent' : 'midGray'}
              customColor={!imageSrc && option.props?.hex}
              dashed={dashed}
              imageSrc={imageSrc}
              isScrollMenu
              isSelected={option.id === value}
              isSmall={false}
              key={option.id ?? 'none'}
              onClick={() => onChange(option.id)}
              onMouseOut={() => setDisplayOption(selectedOption)}
              onMouseOver={() => setDisplayOption(option)}
              text={isSquareWithText && option.name}
            />
          )
        })}
      </div>
      <div className="sub-title">
        <div className="name">{displayOption?.name ?? 'None'}</div>
        <div className="description">
          {displayOption?.description ? displayOption.description : ''}
        </div>
      </div>
    </>
  )
}

function ScrollMenuSection({ name, propId, nodeKind, ...props }) {
  let input
  if (nodeKind === 'SelectNode') {
    input = <SelectInput {...(props as SelectInputProps)} />
  } else if (nodeKind === 'TextNode') {
    input = <TextInput {...props} />
  } else {
    throw Error(`Unknown nodeKind: ${nodeKind}`)
  }

  return (
    <div className="scroll-menu-section">
      <span className="scrollAnchor" id={propId} />
      <h2>{name}</h2>
      {input}
    </div>
  )
}

function getNavSections(navList, nodes) {
  return (
    navListRootIndices
      // First category is the "glove finder", we don't include it in this menu
      .slice(1)
      .map((i) => navList[i])
      .flatMap((category) =>
        category.childIndices
          .map((j) => navList[j])
          .filter((option) => option.isAvailable)
          .flatMap((option) => {
            if (option.childIndices.length === 0) {
              const { propId, name } = option
              const node = nodes[propId]
              if (!node) {
                throw Error(`No node found: ${propId}`)
              }
              return [{ ...node, propId, name }]
            }
            return option.childIndices
              .map((k) => navList[k])
              .filter((subOption) => subOption.isAvailable)
              .map((subOption) => {
                const { propId, name } = subOption
                const node = nodes[propId]
                if (!node) {
                  throw Error(`No node found: ${propId}`)
                }
                return { ...node, propId, name: `${option.name} ${name}` }
              })
          }),
      )
  )
}

export default function ScrollMenu({ navList, nodes, onChange }) {
  const sections = getNavSections(navList, nodes)
  return (
    <div className="scroll-menu">
      <div className="scroll-menu-sections">
        {sections.map((section) => (
          <ScrollMenuSection
            {...section}
            key={section.propId}
            onChange={(value) => onChange(section.keyPath, value)}
          />
        ))}
        <div className="spacer" />
      </div>
    </div>
  )
}
